import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type AppMode = 'POS' | 'WEBSITE' | 'IFRAME';

interface AppModeContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  showLanguageSelector: boolean;
  showWebsiteLinks: boolean;
  useRelativeUrls: boolean;
  isInIframe: boolean;
}

const defaultContext: AppModeContextType = {
  mode: 'WEBSITE',
  setMode: () => {},
  showLanguageSelector: true,
  showWebsiteLinks: true,
  useRelativeUrls: false,
  isInIframe: false,
};

export const AppModeContext = createContext<AppModeContextType>(defaultContext);

export const useAppMode = () => useContext(AppModeContext);

interface AppModeProviderProps {
  children: ReactNode;
  initialMode?: AppMode;
}

// Function to detect if the app is running in an iframe
const detectIframeMode = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    // If we can't access window.top due to cross-origin restrictions,
    // we're likely in an iframe
    return true;
  }
};

export const AppModeProvider: React.FC<AppModeProviderProps> = ({ 
  children,
  initialMode = 'WEBSITE'
}) => {
  const [mode, setMode] = useState<AppMode>(initialMode);
  const [isInIframe, setIsInIframe] = useState<boolean>(false);

  // Detect iframe mode on component mount
  useEffect(() => {
    const iframe = detectIframeMode();
    setIsInIframe(iframe);
    
    // If we detect iframe and no explicit mode is set, switch to IFRAME mode
    if (iframe && initialMode === 'WEBSITE') {
      setMode('IFRAME');
    }
  }, [initialMode]);

  // Derive UI configuration based on mode
  const showLanguageSelector = true; // Show language selector in all modes
  const showWebsiteLinks = mode === 'WEBSITE' || mode === 'IFRAME'; // Show links in WEBSITE and IFRAME modes
  const useRelativeUrls = mode === 'IFRAME'; // Use relative URLs only in IFRAME mode

  return (
    <AppModeContext.Provider value={{ 
      mode, 
      setMode, 
      showLanguageSelector, 
      showWebsiteLinks,
      useRelativeUrls,
      isInIframe
    }}>
      {children}
    </AppModeContext.Provider>
  );
}; 